import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import {
  LocationFilterProps,
  Location,
  LocationType,
} from './LocationFilter.types'
import {
  LocationFilterWrapper,
  InputWrapper,
  SearchIconWrapper,
  ClearButton,
  LocationFilterDropdown,
  LocationList,
  LocationItem,
  LocationName,
  LocationTypeTag,
  NoResults,
  LoadingSpinner,
} from './LocationFilter.styles'
import { Input } from '../../atoms/Input'
import { Icon } from '../../atoms/Icon'

export const LocationFilter = forwardRef<HTMLInputElement, LocationFilterProps>(
  (
    {
      value = '',
      onChange,
      placeholder = 'Search location...',
      minSearchLength = 3,
      maxLocations = 20,
      noResultsText = 'No locations found',
      loadingText: _loadingText = 'Loading...',
      searchTypes = ['region', 'district', 'municipality', 'npa'],
      onSearch,
      size = 'medium',
      error = false,
      fullWidth = false,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(value)
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [locations, setLocations] = useState<Location[]>([])
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const [_selectedLocation, setSelectedLocation] = useState<Location | null>(
      null
    )

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Update input value when value prop changes
    useEffect(() => {
      setInputValue(value)
    }, [value])

    // Expose input ref methods
    useImperativeHandle(ref, () => inputRef.current!, [])

    // Handle click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Handle keyboard navigation
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen || locations.length === 0) return

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault()
            setHighlightedIndex((prev) =>
              prev < locations.length - 1 ? prev + 1 : 0
            )
            break
          case 'ArrowUp':
            e.preventDefault()
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : locations.length - 1
            )
            break
          case 'Enter':
            e.preventDefault()
            if (highlightedIndex >= 0 && highlightedIndex < locations.length) {
              handleLocationSelect(locations[highlightedIndex])
            }
            break
          case 'Escape':
            e.preventDefault()
            setIsOpen(false)
            setHighlightedIndex(-1)
            break
        }
      }

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
      }
    }, [isOpen, locations, highlightedIndex])

    // Search locations with debounce
    const searchLocations = useCallback(
      async (searchValue: string) => {
        if (searchValue.length < minSearchLength) {
          setLocations([])
          setIsOpen(false)
          return
        }

        setLoading(true)
        setIsOpen(true)

        try {
          const results = await onSearch(searchValue, searchTypes)
          setLocations(results.slice(0, maxLocations))
        } catch (error) {
          console.error('Error searching locations:', error)
          setLocations([])
        } finally {
          setLoading(false)
        }
      },
      [minSearchLength, onSearch, searchTypes, maxLocations]
    )

    // Handle input change with debounce
    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setInputValue(newValue)
        setHighlightedIndex(-1)

        // Clear previous timeout
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current)
        }

        // If input is cleared, notify parent
        if (!newValue) {
          setSelectedLocation(null)
          onChange?.(null)
          setLocations([])
          setIsOpen(false)
          return
        }

        // Set new timeout for search
        searchTimeoutRef.current = setTimeout(() => {
          searchLocations(newValue)
        }, 300)
      },
      [searchLocations, onChange]
    )

    // Handle location selection
    const handleLocationSelect = useCallback(
      (location: Location) => {
        setInputValue(location.location_search)
        setSelectedLocation(location)
        setIsOpen(false)
        setHighlightedIndex(-1)
        onChange?.(location)
      },
      [onChange]
    )

    // Handle clear
    const handleClear = useCallback(() => {
      setInputValue('')
      setSelectedLocation(null)
      setLocations([])
      setIsOpen(false)
      setHighlightedIndex(-1)
      onChange?.(null)
      inputRef.current?.focus()
    }, [onChange])

    // Format location type for display
    const formatLocationType = (type: LocationType): string => {
      const typeLabels: Record<LocationType, string> = {
        region: 'Region',
        district: 'District',
        municipality: 'Municipality',
        npa: 'Postal Code',
        address: 'Address',
      }
      return typeLabels[type] || type
    }

    return (
      <LocationFilterWrapper
        ref={containerRef}
        $fullWidth={fullWidth}
        className={className}
      >
        <InputWrapper>
          <SearchIconWrapper>
            <Icon name="search" size="small" />
          </SearchIconWrapper>
          <Input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (
                inputValue.length >= minSearchLength &&
                locations.length > 0
              ) {
                setIsOpen(true)
              }
            }}
            placeholder={placeholder}
            size={size}
            error={error}
            fullWidth
            disabled={disabled}
            {...props}
          />
          {inputValue && !disabled && (
            <ClearButton
              onClick={handleClear}
              type="button"
              aria-label="Clear location"
            >
              <Icon name="x" size="small" />
            </ClearButton>
          )}
        </InputWrapper>

        <LocationFilterDropdown
          ref={dropdownRef}
          $isOpen={isOpen}
          role="listbox"
          aria-label="Location suggestions"
        >
          {loading ? (
            <LoadingSpinner>
              <Icon name="settings" size="medium" />
            </LoadingSpinner>
          ) : locations.length > 0 ? (
            <LocationList>
              {locations.map((location, index) => (
                <LocationItem
                  key={`${location.location_type}-${location.id}`}
                  onClick={() => handleLocationSelect(location)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  $isHighlighted={index === highlightedIndex}
                  role="option"
                  aria-selected={index === highlightedIndex}
                >
                  <LocationName>{location.location_search}</LocationName>
                  <LocationTypeTag $type={location.location_type}>
                    {formatLocationType(location.location_type)}
                  </LocationTypeTag>
                </LocationItem>
              ))}
            </LocationList>
          ) : inputValue.length >= minSearchLength ? (
            <NoResults>{noResultsText}</NoResults>
          ) : null}
        </LocationFilterDropdown>
      </LocationFilterWrapper>
    )
  }
)

LocationFilter.displayName = 'LocationFilter'
